/**
 * The Assist bar's logic (pure, tested in assist.test.ts): what each helper button says (included uses, the Lux
 * price, out of Lux, capped), what a click must do first (nothing, confirm, or the out-of-Lux popover), the roof
 * hints sent and how accepted hints change the drawn faces, and the contextual nudges.
 *
 * Wording (owner, after the Assist UX research): "included", never "free" or "credits"; no price while uses are
 * included; the Lux price and its ≈ $ value once they are used up; drawing is never blocked. Prices come from
 * `GET /v1/designs/{id}/assist` only.
 *
 * What one use buys (owner, Oct 2026): roof hints once per design (the server keeps the roof planes; every later
 * hint on the design, in any session and by anyone, is included: "Already included for this design"); 3D
 * surroundings once per page session (`AssistPageSession`: one Idempotency-Key for the page, so showing them again,
 * toggling or switching 2D/3D never charges again: "Included until you leave this page").
 */
import type { S } from "../api/client";
import type { MessageKey } from "../i18n/keys";
import type { Text } from "./text.ts";
import { faceEave, resolveSlopes, updateFace, type Sketch } from "./skeleton.ts";

export type Feature = S["AssistFeature"];

/** The neutral hover of a helper this dashboard cannot offer (never a configuration note). */
export const NOT_AVAILABLE_YET: MessageKey = "sketch.assist.notAvailableYet";

/** 3D surroundings draw Google's 3D tiles in the browser, so without the dashboard's maps key they are not offered:
 * the button shows disabled, "Not available yet", and nothing can be clicked that could only fail. */
export function withMapsKey(tx: Text, features: Feature[] | null, keyMissing: boolean): Feature[] | null {
  if (!features || !keyMissing) return features;
  return features.map((f) => (f.key === "surroundings" && f.available ? { ...f, available: false, unavailable_reason: tx.t(NOT_AVAILABLE_YET) } : f));
}

const LABEL: Record<string, MessageKey> = { roof_hints: "sketch.action.roofHints", surroundings: "sketch.action.surroundings" };
const DESCRIPTION: Record<string, MessageKey> = { roof_hints: "sketch.assist.describe.roofHints", surroundings: "sketch.assist.describe.surroundings" };

/** A helper's name in the reader's language (the server's English for a helper this dashboard does not know). */
export function featureLabel(tx: Text, f: Pick<Feature, "key" | "label">): string {
  return LABEL[f.key] ? tx.t(LABEL[f.key]!) : f.label;
}

function describe(tx: Text, f: Pick<Feature, "key" | "description">): string {
  return DESCRIPTION[f.key] ? tx.t(DESCRIPTION[f.key]!) : f.description;
}
export type Hints = S["RoofHints"];
export type Hint = S["RoofHint"];

/** Below this many included uses left, the button shows a dot and the tooltip names the price after. */
export const LOW_LEFT = 3;

export type FeatureKind = "included" | "low" | "paid" | "out" | "capped" | "not_sold" | "unavailable" | "design" | "session";

/** Roof hints already bought for this design. */
export const DESIGN_INCLUDED: MessageKey = "sketch.assist.designIncluded";
/** 3D surroundings shown in this page session. */
export const SESSION_INCLUDED: MessageKey = "sketch.assist.sessionIncluded";
/** What a 3D surroundings use lasts (hover and confirmation). */
export const SESSION_NOTE: MessageKey = "sketch.assist.sessionNote";
/** What a roof hints use buys (hover and confirmation). */
export const DESIGN_NOTE: MessageKey = "sketch.assist.designNote";

/** The line the confirmation adds under the price: what the use lasts. */
export function spendNote(tx: Text, key: string): string | null {
  return key === "surroundings" ? tx.t(SESSION_NOTE) : key === "roof_hints" ? tx.t(DESIGN_NOTE) : null;
}

export interface FeatureView {
  kind: FeatureKind;
  /** The small line under the label. */
  subline: string;
  /** The button's tooltip. */
  tooltip: string;
  /** Lux the next click spends (0: included). */
  cost: number;
  /** The click may go ahead (possibly after a confirmation). */
  clickable: boolean;
}

const VERB: Record<string, MessageKey> = { roof_hints: "sketch.assist.verb.roofHints", surroundings: "sketch.assist.verb.surroundings" };

/** The helper's verb line ("Fill pitch & direction"). */
export function verbOf(tx: Text, key: string): string {
  return VERB[key] ? tx.t(VERB[key]!) : "";
}

/** What a helper's button shows, from the server's numbers and this page's session (`sessionActive`: 3D
 * surroundings already granted on this page). */
export function featureView(tx: Text, f: Feature, balance: number | null, ctx: { sessionActive?: boolean } = {}): FeatureView {
  const view = baseView(tx, f, balance, ctx);
  const note = spendNote(tx, f.key);
  // the hover says what a use lasts while a click would still count one
  if (note && view.kind !== "unavailable" && view.kind !== "design" && view.kind !== "session") view.tooltip = tx.t(/[.!?。！？]$/.test(view.tooltip) ? "sketch.assist.tooltipAndNote" : "sketch.assist.tooltipDotNote", { tooltip: view.tooltip, note });
  return view;
}

function baseView(tx: Text, f: Feature, balance: number | null, ctx: { sessionActive?: boolean }): FeatureView {
  // what one use is (owner, Oct 2026): roof hints once per design, 3D surroundings once per page session
  const unit = f.unit === "session" ? "session" : f.key === "roof_hints" ? "design" : "use";
  const price = f.price ?? 0;
  const each = f.price != null ? tx.t("sketch.assist.thenPrice", { price: tx.luxPrice(price), unit }) : tx.t("sketch.assist.thenNone");
  const description = describe(tx, f);
  if (!f.available) return { kind: "unavailable", subline: tx.t("sketch.assist.notAvailable"), tooltip: f.unavailable_reason ?? tx.t("sketch.assist.notAvailableHere"), cost: 0, clickable: false };
  if (f.key === "roof_hints" && f.included_for_design)
    return { kind: "design", subline: tx.t(DESIGN_INCLUDED), tooltip: tx.t("sketch.assist.boughtForDesign", { description }), cost: 0, clickable: true };
  if (f.key === "surroundings" && ctx.sessionActive)
    return { kind: "session", subline: tx.t(SESSION_INCLUDED), tooltip: tx.t("sketch.assist.sessionTooltip"), cost: 0, clickable: true };
  if (f.capped)
    return {
      kind: "capped",
      subline: tx.t(f.capped === "hour" ? "sketch.assist.cappedHour" : "sketch.assist.cappedDay"),
      tooltip: tx.t("sketch.assist.cappedTooltip", { label: featureLabel(tx, f), period: f.capped === "hour" ? "hour" : "day" }),
      cost: 0,
      clickable: false,
    };
  if (f.next_cost === 0) {
    if (f.free_left == null) return { kind: "included", subline: tx.t("sketch.assist.includedInPlan"), tooltip: tx.t("sketch.assist.includedInPlanTooltip", { description }), cost: 0, clickable: true };
    const left = tx.t("sketch.assist.leftThisMonth", { count: f.free_left });
    if (f.free_left <= LOW_LEFT) return { kind: "low", subline: left, tooltip: tx.t("sketch.assist.lowTooltip", { count: f.free_left, unit, then: each }), cost: 0, clickable: true };
    return { kind: "included", subline: left, tooltip: tx.t("sketch.assist.includedTooltip", { description, count: f.free_left, unit }), cost: 0, clickable: true };
  }
  if (f.next_cost == null)
    return { kind: "not_sold", subline: tx.t("sketch.assist.usedUp"), tooltip: tx.t("sketch.assist.usedUpTooltip", { quota: f.free_quota ?? 0, date: tx.date(f.resets_at, { month: "short", day: "numeric" }) }), cost: 0, clickable: false };
  const cost = tx.lux(f.next_cost);
  const tooltip =
    f.price_minor && balance != null ? tx.t("sketch.assist.usesWorthBalance", { lux: cost, money: tx.money(f.price_minor), balance: tx.lux(balance) })
    : f.price_minor ? tx.t("sketch.assist.usesWorth", { lux: cost, money: tx.money(f.price_minor) })
    : balance != null ? tx.t("sketch.assist.usesBalance", { lux: cost, balance: tx.lux(balance) })
    : tx.t("sketch.assist.uses", { lux: cost });
  if (!f.can_pay) return { kind: "out", subline: tx.t("sketch.assist.priceNotEnough", { lux: tx.luxPrice(f.next_cost) }), tooltip, cost: f.next_cost, clickable: true };
  return { kind: "paid", subline: tx.t("sketch.assist.price", { lux: tx.luxPrice(f.next_cost) }), tooltip, cost: f.next_cost, clickable: true };
}

/** Hundredths of a Lux this answer charged now: 0 when included, replayed (the same click again) or reused (the
 * design's earlier purchase). Only a charge shows the Lux toast. */
export function chargedNow(use: { lux: number; replayed?: boolean | null; reused?: boolean | null }): number {
  return use.replayed || use.reused ? 0 : Math.max(use.lux, 0);
}

/** The small line of the hints card: where the data came from and what it cost this design. */
export function hintsCost(tx: Text, use: Hints["use"]): string {
  if (use.reused) return tx.t("sketch.hints.costReused");
  return use.lux > 0 ? tx.t("sketch.hints.costPaid", { lux: tx.lux(use.lux) }) : tx.t("sketch.hints.costIncluded");
}

// ------------------------------------------------------------------------------- 3D surroundings session
/** A granted 3D surroundings session of this page. */
export interface SurroundingsGrant {
  useId: string;
  origin: { lat: number; lng: number };
  groundElevationM: number;
  /** The browser reported that its tiles failed (refunded once). */
  reported?: boolean;
}

/** The page session's Assist state, kept by the page (SketchPage) so it outlives the editor's remount after a save:
 * one Idempotency-Key for 3D surroundings, the session it bought and whether they are shown. Leaving the page ends
 * it; the next page is a new session (a new use). */
export interface AssistPageSession {
  key: string;
  grant: SurroundingsGrant | null;
  on: boolean;
}

export function newPageSession(key: string): AssistPageSession {
  return { key, grant: null, on: false };
}

/** The session is granted and still usable (not reported failed). */
export function sessionActive(s: AssistPageSession): boolean {
  return !!s.grant && !s.grant.reported;
}

export type SurroundingsStep = "hide" | "show" | "wait" | "start";

/** What a click on 3D surroundings does: hide them, show this page's session again (nothing charged), wait for the
 * start already running (a second click), or start a session (the one use of this page). */
export function surroundingsStep(s: AssistPageSession, starting: boolean): SurroundingsStep {
  if (s.on) return "hide";
  if (sessionActive(s)) return "show";
  if (starting) return "wait";
  return "start";
}

/** The page session after a start answered. */
export function granted(s: AssistPageSession, r: { use: { id: string }; origin: { lat: number; lng: number }; ground_elevation_m: number }): AssistPageSession {
  return { ...s, on: true, grant: { useId: r.use.id, origin: r.origin, groundElevationM: r.ground_elevation_m } };
}

/** The page session after its tiles failed and the session was reported (refunded): hidden, and the next start is
 * a new use with a new key (the old key answers its failure). */
export function afterFailure(s: AssistPageSession, nextKey: string): AssistPageSession {
  return { key: nextKey, grant: s.grant ? { ...s.grant, reported: true } : null, on: false };
}

// --------------------------------------------------------------------------------------- roof hints
/** The faces as the hints request sends them: outline, pitch, direction, whether an eave fixes the direction, and
 * whether the installer set the pitch (`locked`: its hint is shown, never applied). */
export function hintRequest(s: Sketch): S["RoofHintsRequest"] {
  const slopes = resolveSlopes(s);
  return {
    faces: s.faces
      .filter((f) => f.boundary.every((v) => s.vertices[v]))
      .map((f) => ({
        id: f.id,
        polygon: f.boundary.map((v) => [round(s.vertices[v]![0]), round(s.vertices[v]![1])]),
        pitch_deg: Math.min(90, Math.max(0, round(slopes[f.id]?.pitch ?? f.pitch))),
        azimuth_deg: (((slopes[f.id]?.azimuth ?? f.azimuth) % 360) + 360) % 360,
        azimuth_from_eave: !!faceEave(s, f),
        locked: f.pitchSource === "set",
      })),
  };
}

const round = (v: number) => Math.round(v * 1000) / 1000;

/** Hints the installer may accept (suggested ones on faces that still exist). */
export function acceptable(hints: readonly Hint[], s: Sketch): Hint[] {
  const ids = new Set(s.faces.map((f) => f.id));
  return hints.filter((h) => h.status === "suggested" && ids.has(h.face_id) && (h.apply_pitch || h.apply_azimuth));
}

/** The sketch with the hints of `faceIds` applied: the pitch (and the direction of a face without an eave);
 * the face's pitch then counts as a hint. A face the installer set is never changed. */
export function applyHints(s: Sketch, hints: readonly Hint[], faceIds: Iterable<string>): Sketch {
  const wanted = new Set(faceIds);
  let next = s;
  for (const h of acceptable(hints, s)) {
    if (!wanted.has(h.face_id)) continue;
    const face = next.faces.find((f) => f.id === h.face_id);
    if (!face || face.pitchSource === "set") continue;
    next = updateFace(next, h.face_id, {
      ...(h.apply_pitch && h.pitch_deg != null ? { pitch: h.pitch_deg } : {}),
      ...(h.apply_azimuth && h.azimuth_deg != null ? { azimuth: h.azimuth_deg } : {}),
      pitchSource: "hint",
    });
  }
  return next;
}

/** "8/12": the way roofers say a pitch (to the half inch). */
export function riseText(tx: Text, deg: number | null | undefined): string {
  if (deg == null) return "—";
  if (deg < 0.5) return tx.t("sketch.pitch.flat");
  return tx.t("sketch.pitch.rise", { rise: tx.upTo(Math.round(Math.tan((deg * Math.PI) / 180) * 24) / 2, 1) });
}

/** "8/12 · 33.7°": the roofers' pitch with the degrees. */
export function pitchText(tx: Text, deg: number | null | undefined): string {
  if (deg == null || deg < 0.5) return riseText(tx, deg);
  return tx.t("sketch.pitch.riseAndDegrees", { rise: riseText(tx, deg), deg: tx.num(deg, 1) });
}

/** One line per face for the review list, in the order the faces were drawn. */
export function hintRows(tx: Text, hints: Hints, s: Sketch, labels: Record<string, string>): { hint: Hint; label: string; change: string; tone: "suggested" | "ok" | "locked" | "none" }[] {
  const order = new Map(s.faces.map((f, i) => [f.id, i]));
  return hints.faces
    .filter((h) => order.has(h.face_id))
    .sort((a, b) => order.get(a.face_id)! - order.get(b.face_id)!)
    .map((h) => {
      const label = labels[h.face_id] ?? h.face_id;
      if (h.status === "no_match") return { hint: h, label, change: tx.t("sketch.hints.noMatch"), tone: "none" as const };
      if (h.status === "locked") return { hint: h, label, change: tx.t("sketch.hints.locked", { pitch: pitchText(tx, h.pitch_deg) }), tone: "locked" as const };
      if (h.status === "matches") return { hint: h, label, change: tx.t("sketch.hints.matches", { pitch: pitchText(tx, h.current_pitch_deg) }), tone: "ok" as const };
      const pitch = h.apply_pitch ? tx.t("sketch.hints.pitchChange", { from: riseText(tx, h.current_pitch_deg), to: pitchText(tx, h.pitch_deg) }) : null;
      const facing = h.apply_azimuth ? tx.t("sketch.hints.facing", { deg: tx.num(Math.round(h.azimuth_deg ?? 0), 0) }) : null;
      const change = pitch && facing ? tx.t("sketch.hints.pitchAndFacing", { pitch, facing }) : pitch ?? facing ?? "";
      return { hint: h, label, change, tone: "suggested" as const };
    });
}

// ------------------------------------------------------------------------------------------ nudges
export type NudgeKey = "roof_hints" | "surroundings";

/** The first face still at the drawing's default pitch, where the "Fill from roof data" chip goes; null when
 * there is none, hints were already asked for in this session, or the nudge was dismissed for this design. */
export function hintNudgeFace(s: Sketch, opts: { asked: boolean; dismissed: boolean; available: boolean }): string | null {
  if (opts.asked || opts.dismissed || !opts.available) return null;
  const face = s.faces.find((f) => !f.pitchSource || f.pitchSource === "drawn");
  return face?.id ?? null;
}

/** The roof hints nudge's words: once hints were bought for this design (another session, a colleague) it says they
 * are included, so a new session never reads as an offer to pay again. */
export function hintNudgeLabel(tx: Text, feature: { included_for_design?: boolean | null } | null | undefined): string {
  return tx.t(feature?.included_for_design ? "sketch.assist.nudgeIncluded" : "sketch.assist.nudge");
}

/** Whether to offer "Add surroundings" when the 3D view opens (not once this page has a session: the button
 * shows them again). */
export function surroundingsNudge(opts: { view: "2d" | "3d"; on: boolean; shown: boolean; dismissed: boolean; available: boolean; granted?: boolean }): boolean {
  return opts.view === "3d" && !opts.on && !opts.granted && !opts.shown && !opts.dismissed && opts.available;
}

/** The localStorage key that keeps a dismissed nudge off for a design (per viewer). */
export function nudgeStorageKey(designId: string, key: NudgeKey): string {
  return `solaros.sketch.nudge.${key}.${designId}`;
}
